import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchStylesList } from '@/api/my-styles';
import type { StylesListParams } from '@/api/my-styles/types';
import { useNetworkCompanyMap } from '@/hooks/useDropdowns';

export interface StylesCreatedForMeQueryInput {
  page: number;
  limit: number;
  sort?: string;
  filters?: Record<string, string[]>;
}

function parseSort(sort?: string): { sort: string; order: 'ASC' | 'DESC' } {
  if (!sort) return { sort: 'updatedAt', order: 'DESC' };
  const [field, dir] = sort.split(':');
  if (!field) return { sort: 'updatedAt', order: 'DESC' };
  return { sort: field, order: dir === 'DESC' ? 'DESC' : 'ASC' };
}

function nonEmpty(arr?: string[]): string[] | undefined {
  return arr && arr.length > 0 ? arr : undefined;
}

function buildApiParams(input: StylesCreatedForMeQueryInput): StylesListParams {
  const parsed = parseSort(input.sort);
  const f = input.filters ?? {};

  const suppliers = nonEmpty(f.suppliers);
  const vendors = nonEmpty(f.vendors);
  const brands = nonEmpty(f.brands);
  const departments = nonEmpty(f.departments);
  const seasons = nonEmpty(f.seasons);
  const collections = nonEmpty(f.collections);

  return {
    page: input.page,
    limit: input.limit,
    // styleLink + tracingStory populate `linkedStyles` and `tracingStory` on each row.
    include: [
      'type',
      'styleProperties',
      'material',
      'styleLink',
      'tracingStory',
    ],
    createdForMe: true,
    sort: parsed.sort,
    order: parsed.order,
    ...(f.name?.[0] !== undefined ? { name: f.name[0] } : {}),
    ...(f.styleTypeId?.[0] !== undefined
      ? { styleTypeId: f.styleTypeId[0] }
      : {}),
    ...(f.mainSupplier?.[0] !== undefined
      ? { mainSupplier: f.mainSupplier[0] }
      : {}),
    ...(suppliers !== undefined ? { suppliers } : {}),
    ...(vendors !== undefined ? { vendors } : {}),
    ...(brands !== undefined ? { brands } : {}),
    ...(departments !== undefined ? { departments } : {}),
    ...(seasons !== undefined ? { seasons } : {}),
    ...(collections !== undefined ? { collections } : {}),
  };
}

export function stylesCreatedForMeQueryOptions(
  input: StylesCreatedForMeQueryInput,
) {
  const apiParams = buildApiParams(input);
  return queryOptions({
    queryKey: ['my-styles-created-for-me', 'list', apiParams] as const,
    queryFn: () => fetchStylesList(apiParams),
  });
}

export function useStylesCreatedForMeQuery(
  input: StylesCreatedForMeQueryInput,
) {
  const query = useQuery(stylesCreatedForMeQueryOptions(input));
  const companyMap = useNetworkCompanyMap();
  return { query, companyMap };
}
